export type AnchorSide = 'top' | 'right' | 'bottom' | 'left';

export type LayoutStrategy = 'left-to-right' | 'top-to-bottom' | 'hierarchical' | 'compact';

export type Point = { x: number; y: number };

export type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

export function rectFromNode(
  node: { x?: number; y?: number; w?: number; width?: number; h?: number; height?: number },
  pos?: { x: number; y: number },
  defaults: { w: number; h: number } = { w: 120, h: 80 }
): Rect {
  const w = (node.w ?? node.width ?? defaults.w) as number;
  const h = (node.h ?? node.height ?? defaults.h) as number;
  const x = (pos?.x ?? node.x ?? 0) as number;
  const y = (pos?.y ?? node.y ?? 0) as number;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
}

export function anchorSideFromPoint(rect: Rect, point: Point): AnchorSide {
  const epsilon = 6;
  const left = rect.x;
  const right = rect.x + rect.w;
  const top = rect.y;
  const bottom = rect.y + rect.h;

  if (Math.abs(point.x - left) <= epsilon) return 'left';
  if (Math.abs(point.x - right) <= epsilon) return 'right';
  if (Math.abs(point.y - top) <= epsilon) return 'top';
  if (Math.abs(point.y - bottom) <= epsilon) return 'bottom';

  const dLeft = Math.abs(point.x - left);
  const dRight = Math.abs(point.x - right);
  const dTop = Math.abs(point.y - top);
  const dBottom = Math.abs(point.y - bottom);

  const min = Math.min(dLeft, dRight, dTop, dBottom);
  if (min === dLeft) return 'left';
  if (min === dRight) return 'right';
  if (min === dTop) return 'top';
  return 'bottom';
}

function sideCenter(r: Rect, side: AnchorSide): { x: number; y: number } {
  switch (side) {
    case 'top':
      return { x: r.cx, y: r.y };
    case 'right':
      return { x: r.x + r.w, y: r.cy };
    case 'bottom':
      return { x: r.cx, y: r.y + r.h };
    case 'left':
      return { x: r.x, y: r.cy };
  }
}

export function chooseAnchorPairForStrategy(
  a: Rect,
  b: Rect,
  strategy: LayoutStrategy
): { aSide: AnchorSide; bSide: AnchorSide } {
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;

  const horizontal = dx >= 0
    ? ({ aSide: 'right', bSide: 'left' } as const)
    : ({ aSide: 'left', bSide: 'right' } as const);
  const vertical = dy >= 0
    ? ({ aSide: 'bottom', bSide: 'top' } as const)
    : ({ aSide: 'top', bSide: 'bottom' } as const);

  const ha = sideCenter(a, horizontal.aSide);
  const hb = sideCenter(b, horizontal.bSide);
  const va = sideCenter(a, vertical.aSide);
  const vb = sideCenter(b, vertical.bSide);

  const dh2 = (ha.x - hb.x) ** 2 + (ha.y - hb.y) ** 2;
  const dv2 = (va.x - vb.x) ** 2 + (va.y - vb.y) ** 2;

  const horizontalBias = strategy === 'left-to-right';
  const verticalBias = strategy === 'top-to-bottom' || strategy === 'hierarchical';

  const hWeight = verticalBias ? 1.15 : 1.0;
  const vWeight = horizontalBias ? 1.15 : 1.0;

  return dh2 * hWeight <= dv2 * vWeight ? horizontal : vertical;
}
